import React from 'react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  icon?: React.ReactNode;
}

export const Input: React.FC<InputProps> = ({
  label,
  error,
  icon,
  className = '',
  id,
  ...props
}) => {
  const inputId = id || props.name || Math.random().toString(36).substr(2, 9);
  
  const baseInputStyles = 'h-11 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm transition-colors focus:outline-none focus:border-slate-300 focus:ring-0 placeholder:text-slate-300 dark:placeholder:text-slate-500 disabled:opacity-50 disabled:cursor-not-allowed';
  
  const paddingStyles = icon ? 'pl-11 pr-4' : 'px-4';
  
  return (
    <div className="space-y-1.5">
      {label && (
        <label 
          htmlFor={inputId}
          className="block text-ds-meta text-slate-500 dark:text-slate-400"
        >
          {label}
        </label>
      )}
      <div className="relative">
        {icon && (
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
            {icon}
          </span>
        )}
        <input
          id={inputId}
          className={`${baseInputStyles} ${paddingStyles} ${className}`}
          {...props}
        />
      </div>
      {error && (
        <p className="text-ds-meta text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
};

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
}

export const Textarea: React.FC<TextareaProps> = ({
  label,
  error,
  className = '',
  id,
  ...props
}) => {
  const inputId = id || props.name || Math.random().toString(36).substr(2, 9);
  
  const baseStyles = 'w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm transition-colors focus:outline-none focus:border-slate-300 focus:ring-0 placeholder:text-slate-300 dark:placeholder:text-slate-500 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-3';
  
  return (
    <div className="space-y-1.5">
      {label && (
        <label 
          htmlFor={inputId}
          className="block text-ds-meta text-slate-500 dark:text-slate-400"
        >
          {label}
        </label>
      )}
      <textarea
        id={inputId}
        className={`${baseStyles} ${className}`}
        {...props}
      />
      {error && (
        <p className="text-ds-meta text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
};

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  options: { value: string; label: string }[];
}

export const Select: React.FC<SelectProps> = ({
  label,
  error,
  options,
  className = '',
  id,
  ...props
}) => {
  const inputId = id || props.name || Math.random().toString(36).substr(2, 9);
  
  const baseStyles = 'h-11 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm transition-colors focus:outline-none focus:border-slate-300 focus:ring-0 px-4 disabled:opacity-50 disabled:cursor-not-allowed';
  
  return (
    <div className="space-y-1.5">
      {label && (
        <label 
          htmlFor={inputId}
          className="block text-ds-meta text-slate-500 dark:text-slate-400"
        >
          {label}
        </label>
      )}
      <select
        id={inputId}
        className={`${baseStyles} ${className}`}
        {...props}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && (
        <p className="text-ds-meta text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
};

export default Input;
